import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { ArrowLeftIcon } from "@heroicons/react/outline";
import SlideOver from "../../Modal/SlideOver";
import Button from "../../Button";
import FormSelect from "../../Form/FormSelect";
import DepositAllocationList from "../DepositAllocationList";
import {
  applyDeposit,
  fetchAccountingTransaction,
  fetchUnpaidPartnerInvoices,
  listDeposits,
  usePeriodLock,
} from "../../../../Context";
import { formatCurrency } from "../../../../Utils";
import {
  buildDefaultAllocations,
  getInvoiceSettlement,
  getDepositActionError,
  getDepositApplyDate,
  isDepositOpen,
  sortDepositsOldestFirst,
  sumAllocations,
  validateAllocations,
} from "../../../../Utils/DepositUtil";
import { useInvoiceTotals } from "../../../../Hooks/useInvoiceTotals";
import FormError from "../../Form/FormError";

/**
 * W6 · Pakai Uang Muka. Takes the place of the panel it was opened from, the
 * way Bayar does on an invoice, and ← goes back to it.
 *
 * Opened from an invoice it starts with that invoice; opened from a deposit
 * (`invoice` absent, `partner` given) it first asks which of the customer's
 * unpaid invoices to use, and `depositID` is the only one ticked by default.
 *
 * No confirmation dialog: every application can be undone, from the banner
 * the invoice shows afterwards and from the deposit's usage history.
 *
 * `onApplied(results)` receives `{ depositID, depositNumber, usageId, amount }`
 * for every application that went through — also when a later one failed, so
 * the caller can still offer to undo the ones that did.
 */
const DepositApplyPanel = ({
  open,
  onBack,
  onClose,
  invoice: invoiceProp,
  partner,
  depositID,
  businessID,
  onApplied,
}) => {
  const { t, i18n } = useTranslation();

  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [invoicesTruncated, setInvoicesTruncated] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [pickedInvoice, setPickedInvoice] = useState(null);

  const [deposits, setDeposits] = useState([]);
  const [depositsLoading, setDepositsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [allocations, setAllocations] = useState({});
  // The invoice `allocations` were filled for. The list is shown only once
  // this matches the invoice on screen, so the default (oldest first) is never
  // written after the user has started ticking.
  const [allocationsFor, setAllocationsFor] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  // The invoice as re-read after some of the applications went through, so
  // its balance is current before the user tries the rest again.
  const [freshInvoice, setFreshInvoice] = useState(null);
  const [reloading, setReloading] = useState(false);

  const baseInvoice = invoiceProp || pickedInvoice;
  const invoice =
    freshInvoice && freshInvoice.id === baseInvoice?.id
      ? freshInvoice
      : baseInvoice;
  const partnerID = invoice?.partnerID || partner?.id;
  const partnerName = invoice?.partner?.name || partner?.name || "";

  const { isDateClosed, closedThroughLabel } = usePeriodLock(businessID, {
    locale: i18n.language,
  });

  const { settlementAmount: balance } = useInvoiceTotals(invoice);

  // Fills the default in the same step that brings in the data it depends on.
  // Opened from one deposit, that deposit alone: ticking the customer's others
  // too could spend them before the one the user meant.
  const depositIDRef = useRef(depositID);
  depositIDRef.current = depositID;
  const fillDefault = (list, target) => {
    const only = depositIDRef.current;
    const candidates = only ? list.filter((d) => d.id === only) : list;
    setAllocations(
      target
        ? buildDefaultAllocations(candidates, getInvoiceSettlement(target))
        : {},
    );
    setAllocationsFor(target?.id ?? null);
  };
  const invoiceRef = useRef(invoice);
  invoiceRef.current = invoice;

  // Unpaid invoices of the customer, only when we were not handed one.
  useEffect(() => {
    if (!open || invoiceProp || !partnerID) return;
    let live = true;
    setInvoicesLoading(true);
    fetchUnpaidPartnerInvoices(partnerID)
      .then(({ items, truncated }) => {
        if (live) {
          setInvoiceOptions(items);
          setInvoicesTruncated(truncated);
        }
      })
      .catch(() => {
        if (live) {
          setInvoiceOptions([]);
          setInvoicesTruncated(false);
        }
      })
      .finally(() => {
        if (live) setInvoicesLoading(false);
      });
    return () => {
      live = false;
    };
  }, [open, invoiceProp, partnerID]);

  useEffect(() => {
    if (!open || !partnerID || !businessID) return;
    let live = true;
    setDepositsLoading(true);
    setLoadError(null);
    listDeposits({ businessID, partnerID, status: "open" })
      .then(({ items }) => {
        if (live) {
          const list = sortDepositsOldestFirst(items.filter(isDepositOpen));
          setDeposits(list);
          fillDefault(list, invoiceRef.current);
        }
      })
      .catch((error) => {
        if (live) setLoadError(error);
      })
      .finally(() => {
        if (live) setDepositsLoading(false);
      });
    return () => {
      live = false;
    };
  }, [open, partnerID, businessID]);

  // The default is filled once per invoice, oldest first and capped at the
  // balance; after that the ticks and amounts are the user's.
  useEffect(() => {
    if (!open) {
      setAllocations({});
      setAllocationsFor(null);
      setPickedInvoice(null);
      setFreshInvoice(null);
      setSubmitError("");
      setShowErrors(false);
    }
  }, [open]);

  const allocationsReady = Boolean(invoice) && allocationsFor === invoice.id;

  const errors = useMemo(
    () => validateAllocations(allocations, deposits, balance),
    [allocations, deposits, balance],
  );
  const total = sumAllocations(allocations);

  const selected = deposits.filter((d) => allocations[d.id] !== undefined);
  // The posting date is fixed: the invoice's, or a later deposit's.
  const applyDates = selected.map((d) =>
    getDepositApplyDate(invoice?.createdAt, d.createdAt),
  );
  const shownDate =
    applyDates.reduce(
      (latest, date) => (date > latest ? date : latest),
      null,
    ) || (invoice?.createdAt ? new Date(invoice.createdAt) : null);
  const dateLocked =
    applyDates.some((date) => isDateClosed(date)) ||
    (selected.length === 0 && invoice && isDateClosed(invoice.createdAt));

  const canSubmit =
    invoice &&
    total > 0 &&
    Object.keys(errors).length === 0 &&
    !dateLocked &&
    !reloading;

  // After a partial failure: the deposits that went through come off the
  // form, and the invoice and the deposits are read again, so trying again
  // sends only the rest and checks it against what is really left. Without
  // this the same deposit was applied twice.
  const reloadAfterPartial = async (results) => {
    const done = new Set(results.map((r) => r.depositID));
    setAllocations((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([id]) => !done.has(id)),
      ),
    );
    setReloading(true);
    try {
      const [refreshed, { items }] = await Promise.all([
        fetchAccountingTransaction(invoice.id),
        listDeposits({ businessID, partnerID, status: "open" }),
      ]);
      if (refreshed) setFreshInvoice(refreshed);
      const list = sortDepositsOldestFirst(items.filter(isDepositOpen));
      setDeposits(list);
      // A deposit used up by the part that went through is no longer listed.
      const listed = new Set(list.map((d) => d.id));
      setAllocations((prev) =>
        Object.fromEntries(
          Object.entries(prev).filter(([id]) => listed.has(id)),
        ),
      );
    } catch (error) {
      // The used deposits are off the form already; the rest is checked
      // against the stale figures, and the server has the last word.
    } finally {
      setReloading(false);
    }
  };

  const handleSubmit = async () => {
    setShowErrors(true);
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setSubmitError("");
    const results = [];
    try {
      for (const deposit of selected) {
        const amount = Math.round(allocations[deposit.id]);
        // One at a time, oldest first: each answer can change what the next
        // is allowed to take.
        // eslint-disable-next-line no-await-in-loop
        const data = await applyDeposit(deposit.id, {
          invoiceID: invoice.id,
          amount,
        });
        results.push({
          depositID: deposit.id,
          depositNumber: deposit.depositNumber,
          usageId: data?.usageId,
          amount,
        });
      }
      setSubmitting(false);
      onApplied?.(results, invoice);
    } catch (error) {
      setSubmitting(false);
      setSubmitError(getDepositActionError(error, t));
      if (results.length) {
        onApplied?.(results, invoice, { partial: true });
        await reloadAfterPartial(results);
      }
    }
  };

  const invoiceSelectOptions = useMemo(
    () =>
      invoiceOptions.map((item) => ({
        ...item,
        value: item.id,
        label: `#${item.invoiceNumber || "-"} · ${dayjs(item.createdAt)
          .locale(i18n.language)
          .format("DD MMM YYYY")}`,
      })),
    [invoiceOptions, i18n.language],
  );

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      // Picking an invoice is a choice made here that no input or change event
      // reports, so say so: a stray click outside then asks before throwing
      // the choice away instead of closing the panel and the deposit with it.
      dirty={Boolean(pickedInvoice)}
      title="deposits.apply.title"
      actionButton={
        <button
          type="button"
          className="rounded-md bg-white text-gray-400 hover:text-gray-500"
          onClick={onBack}
        >
          <span className="sr-only">{t("common.back")}</span>
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
      }
      footer={
        <div className="flex flex-col gap-2">
          <FormError error={submitError} />
          <div className="flex items-center justify-end gap-3">
            <Button variant="secondary" onClick={onBack}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              loading={submitting}
              disabled={!canSubmit}
            >
              {t("deposits.apply.submit", {
                interpolation: { escapeValue: false },
                amount: formatCurrency(total),
              })}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-5 mb-6">
        {!invoiceProp && (
          <FormSelect
            label="deposits.apply.chooseInvoice"
            searchable
            options={invoiceSelectOptions}
            value={
              invoiceSelectOptions.find((o) => o.value === pickedInvoice?.id) ||
              null
            }
            onChange={(option) => {
              setPickedInvoice(option || null);
              setFreshInvoice(null);
              fillDefault(deposits, option || null);
            }}
            // Opened straight from a list row, the customer arrives with the
            // deposit a moment later; until then the list is still coming.
            isLoading={invoicesLoading || !partnerID}
            placeholder="deposits.placeholders.invoice"
          />
        )}
        {!invoiceProp && partnerID && !invoicesLoading && invoicesTruncated && (
          <p className="text-xs text-gray-500">
            {t("deposits.apply.invoicesTruncated")}
          </p>
        )}
        {!invoiceProp &&
          partnerID &&
          !invoicesLoading &&
          !invoicesTruncated &&
          invoiceOptions.length === 0 && (
          <p className="text-sm text-gray-500">
            {t("deposits.apply.noInvoices", {
              interpolation: { escapeValue: false },
              partner: partnerName,
            })}
          </p>
        )}

        {invoice && (
          <>
            <div className="flex items-center justify-between rounded-lg border border-gray-100 bg-white px-4 py-3 shadow">
              <div>
                <p className="text-xs font-medium text-gray-500">
                  {t("invoices.detail.fields.invoiceNumber")}
                </p>
                <p className="text-sm font-medium text-gray-900">
                  #{invoice.invoiceNumber || "-"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs font-medium text-gray-500">
                  {t("invoices.detail.fields.balanceDue")}
                </p>
                <p className="text-sm font-medium tabular-nums text-gray-900">
                  {formatCurrency(balance)}
                </p>
              </div>
            </div>

            {loadError ? (
              <FormError error={getDepositActionError(loadError, t)} />
            ) : depositsLoading || reloading || !allocationsReady ? (
              <div className="h-24 animate-pulse rounded-md bg-gray-100" />
            ) : deposits.length === 0 ? (
              <p className="text-sm text-gray-500">
                {t("deposits.apply.none")}
              </p>
            ) : (
              <DepositAllocationList
                deposits={deposits}
                allocations={allocations}
                onChange={setAllocations}
                limit={balance}
                errors={showErrors ? errors : {}}
                disabled={submitting}
                idPrefix="apply-deposit"
              />
            )}
            {errors.total && (
              <p className="text-sm text-red-600">{t(errors.total)}</p>
            )}

            <div>
              <p className="text-sm font-medium text-gray-700">
                {t("deposits.apply.date")}
              </p>
              <p className="mt-1 text-sm text-gray-900">
                {shownDate
                  ? dayjs(shownDate).locale(i18n.language).format("DD MMM YYYY")
                  : "-"}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {t("deposits.apply.dateHint")}
              </p>
            </div>

            {dateLocked && (
              <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
                {t("deposits.apply.periodClosed", {
                  interpolation: { escapeValue: false },
                  date: closedThroughLabel,
                })}
              </p>
            )}

            <dl className="space-y-1 border-t border-gray-200 pt-3 text-sm">
              <div className="flex justify-between">
                <dt>{t("deposits.invoiceForm.used")}</dt>
                <dd className="tabular-nums">−{formatCurrency(total)}</dd>
              </div>
              <div className="flex justify-between font-semibold">
                <dt>{t("deposits.apply.balanceAfter")}</dt>
                <dd className="tabular-nums">
                  {formatCurrency(Math.max(0, balance - total))}
                </dd>
              </div>
            </dl>
          </>
        )}
      </div>
    </SlideOver>
  );
};

export default DepositApplyPanel;
